import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { demoApps } from "../data/demo";
import { api, demoMode } from "../lib/api";
import type { AppRecord, AppleEnvironment } from "../types";

interface OpsContextValue {
  apps: AppRecord[];
  selectedApp?: AppRecord;
  selectedAppId: string;
  setSelectedAppId: (id: string) => void;
  environment: AppleEnvironment;
  setEnvironment: (environment: AppleEnvironment) => void;
  days: number;
  setDays: (days: number) => void;
  appsLoading: boolean;
}

const OpsContext = createContext<OpsContextValue | null>(null);

export function OpsProvider({ children }: { children: ReactNode }) {
  const appsQuery = useQuery({
    queryKey: ["apps"],
    queryFn: () => demoMode ? Promise.resolve({ data: demoApps }) : api<{ data: AppRecord[] }>("/api/apps"),
  });
  const apps = useMemo(() => appsQuery.data?.data ?? [], [appsQuery.data]);
  const [selectedAppId, setSelectedAppIdState] = useState(() => localStorage.getItem("ops-app-id") ?? "");
  const [environment, setEnvironmentState] = useState<AppleEnvironment>(() => localStorage.getItem("ops-environment") === "sandbox" ? "sandbox" : "production");
  const [days, setDays] = useState(30);

  const value = useMemo<OpsContextValue>(() => ({
    apps,
    selectedApp: apps.find((app) => app.id === selectedAppId) ?? apps[0],
    selectedAppId: apps.some((app) => app.id === selectedAppId) ? selectedAppId : apps[0]?.id ?? "",
    setSelectedAppId: (id) => { localStorage.setItem("ops-app-id", id); setSelectedAppIdState(id); },
    environment,
    setEnvironment: (next) => { localStorage.setItem("ops-environment", next); setEnvironmentState(next); },
    days,
    setDays,
    appsLoading: appsQuery.isLoading,
  }), [apps, selectedAppId, environment, days, appsQuery.isLoading]);

  return <OpsContext.Provider value={value}>{children}</OpsContext.Provider>;
}

export function useOps() {
  const value = useContext(OpsContext);
  if (!value) throw new Error("useOps must be used inside OpsProvider");
  return value;
}
